import { useCallback, useEffect, useState } from "react";

export interface ToastProps {
  id?: number;
  title?: string;
  description?: string;
  variant?: "default" | "destructive" | "warning" | "success";
  duration?: number;
}

type Listener = (toasts: ToastProps[]) => void;

// Global toast store so all hooks share the same queue
let toastQueue: ToastProps[] = [];
const listeners: Set<Listener> = new Set();

function notify() {
  for (const l of listeners) l(toastQueue);
}

function addToast(t: Omit<ToastProps, "id">) {
  const id = Date.now() + Math.floor(Math.random() * 1000);
  const toast: ToastProps = { id, variant: "default", duration: 3000, ...t };
  toastQueue = [...toastQueue, toast];
  notify();
  const duration = toast.duration ?? 3000;
  setTimeout(() => {
    toastQueue = toastQueue.filter((x) => x.id !== id);
    notify();
  }, duration);
}

export function useToast() {
  const [toasts, setToasts] = useState<ToastProps[]>(toastQueue);

  useEffect(() => {
    const listener: Listener = (q) => setToasts(q);
    listeners.add(listener);
    // Initialize
    setToasts(toastQueue);
    return () => {
      listeners.delete(listener);
    };
  }, []);

  const toast = useCallback((t: Omit<ToastProps, "id">) => addToast(t), []);

  return { toast, toasts };
}
